import {useEffect, useState} from 'react'
import {useNotification} from 'hooks/useNotification'
import {useCreateBlackoutsBulkMutation, useGetLocationsQuery} from 'services/locations'
import {useLocation, useNavigate} from 'react-router-dom'
import {getDateAddedTwoDays, getDateStringFormat} from 'utils/date'
import AutoComplete, {OptionType} from 'components/ui/AutoComplete'
import BlackoutPeriodsList from 'components/ui/BlackoutPeriodsList'
import interactionPlugin from '@fullcalendar/interaction'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import dayGridPlugin from '@fullcalendar/daygrid'
import FullCalendar from '@fullcalendar/react'
import TextField from 'components/ui/TextField'
import Button from 'components/ui/Button'
import Footer from 'layouts/components/Breakout/Footer'
import useMedia from 'use-media'
import Spinner from 'components/ui/Spinner'
import {IBlackoutBulkRequest} from "../../../interfaces/locations";
import {enqueueSnackbar} from "notistack";

interface ILocationsState {
  locations: number[]
}

export interface IBlackoutPeriod {
  startDate: string | null
  endDate: string | null
  startTime: string | null
  endTime: string | null
  startDateComplete: Date
  endDateComplete: Date
}

const Blackouts = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useMedia({ maxWidth: '640px' })
  const { locations: locationsState } = location.state as ILocationsState
  const { sendErrorNotification } = useNotification()
  const getEventsOptions = (
    newStartDate?: string | null,
    newEndDate?: string
  ) => {
    return [
      {
        start: newStartDate || startDate,
        end: newEndDate || endDate,
        display: 'background',
        backgroundColor: '#2176ef'
      }
    ]
  }

  const [startDate, setStartDate] = useState<string | null>(() =>
    getDateStringFormat(new Date())
  )
  const [endDate, setEndDate] = useState<string | null>(startDate)
  const [startTime, setStartTime] = useState<string | null>('00:00')
  const [endTime, setEndTime] = useState<string | null>('23:59')

  const [dateSelected, setDateSelected] = useState<any[]>(getEventsOptions())
  const [eventsCalendar, setEventsCalendar] = useState<any[]>(
    getEventsOptions()
  )
  const [blackoutSelected, setBlackoutSelected] = useState<IBlackoutPeriod[]>(
    []
  )
  const { data: dataLocations, isLoading: isLoadingLocations } =
    useGetLocationsQuery({
      limit: 999
    })
  const [locationsSelected, setLocationsSelected] = useState<OptionType[]>([])
  const locations: OptionType[] =
    dataLocations?.locations.map((row) => {
      return {
        label: row.name,
        id: row.id
      }
    }) || []
  const [createBlackoutsBulk, { isLoading: isCreatingBlackouts }] = useCreateBlackoutsBulkMutation()

  const handleLocationsChange = (
    _event: React.ChangeEvent<{}>,
    value: OptionType[]
  ) => {
    setLocationsSelected(value)
  }

  const handleSelect = (info: any) => {
    let infoEndDate = new Date(info.endStr)
    const correctEndDate = getDateStringFormat(infoEndDate)

    setDateSelected(getEventsOptions(info.startStr, info.endStr))
    setEventsCalendar(getEventsOptions(info.startStr, info.endStr))
    setStartDate(info.startStr)
    setEndDate(correctEndDate)
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStartDate(event.target.value)
    setDateSelected(getEventsOptions(event.target.value))

    const addedTwoDaysToCalendarColor = getDateAddedTwoDays(endDate as string)
    setEventsCalendar(
      getEventsOptions(event.target.value, addedTwoDaysToCalendarColor)
    )
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value)

    const correctEndDateCalendar = getDateAddedTwoDays(event.target.value)
    setDateSelected(getEventsOptions(startDate, event.target.value))
    setEventsCalendar(getEventsOptions(startDate, correctEndDateCalendar))
  }

  const handleBlackoutClick = () => {
    const startDateComplete = new Date(`${startDate} ${startTime}`)
    const endDateComplete = new Date(`${endDate} ${endTime}`)
    const existBlackout = blackoutSelected.find((blackout) => {
      return (
        blackout.startDate === startDate &&
        blackout.endDate === endDate &&
        blackout.startTime === startTime &&
        blackout.endTime === endTime
      )
    })

    if (existBlackout) {
      sendErrorNotification('Blackout period already exists')
      return
    }

    setBlackoutSelected([
      ...blackoutSelected,
      {
        startDate,
        endDate,
        startTime,
        endTime,
        startDateComplete,
        endDateComplete
      }
    ])
  }

  const handleDeleteBlackout = (index: number) => {
    const newBlackoutSelected = blackoutSelected.filter(
      (_blackout, i) => i !== index
    )
    setBlackoutSelected(newBlackoutSelected)
  }


  const handleOnSaveBlackout = async () => {
    if (locationsSelected.length === 0 || blackoutSelected.length === 0) {
      sendErrorNotification('Please select locations and blackout periods')
      return
    }

    const blackoutRequest: IBlackoutBulkRequest = {
      location_ids: locationsSelected.map(location => location.id.toString()),
      blackout_slots: blackoutSelected.map(blackout => ({
        date_start: blackout.startDate!,
        date_end: blackout.endDate!,
        working_time_start: blackout.startTime!,
        working_time_end: blackout.endTime!,
      }))
    }

    try {
      const response: any = await createBlackoutsBulk(blackoutRequest).unwrap()
      if(response.status){
        enqueueSnackbar("Successfully added blackout slot", { variant: 'success' });
        navigate('/admin/locations')
      }else{
        enqueueSnackbar("There was an issue adding the blackout slot", { variant: 'error' });
      }
    } catch (error) {
      console.error('Failed to create blackouts:', error)
      sendErrorNotification('Failed to create blackouts. Please try again.')
    }
  }

  useEffect(() => {
    if (dataLocations) {
      const locationsSelectedDefault = locationsState.map(
        (location: number) => {
          const rowExist = dataLocations?.locations.find(
            (row) => row.id === String(location)
          )
          return {
            label: rowExist?.name || '',
            id: location
          }
        }
      )
      setLocationsSelected(locationsSelectedDefault)
    }
  }, [dataLocations, locationsState])

  return (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-2 h-full container mx-auto">
          <div className="flex flex-col gap-10 border-r-2 p-6 lg:pb-32">
            <section>
              <h3 className="font-semibold mb-5">1. Selected Locations</h3>

              {isLoadingLocations ? (
                  <Spinner/>
              ) : (
                  <AutoComplete
                      id="locations"
                      multipleOptions
                      label="Locations"
                      placeholder="Select location"
                      value={locationsSelected}
                      onChange={handleLocationsChange}
                      options={locations}
                      limitTags={isMobile ? 5 : 20}
                  />
              )}
            </section>

            <section>
              <h3 className="font-semibold mb-5">2. Select Dates to Blackout</h3>
              <FullCalendar
                  plugins={[interactionPlugin, dayGridPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: 'prev,next today',
                    right: 'title'
                  }}
                  select={handleSelect}
                  selectable
                  events={eventsCalendar}
                  longPressDelay={100}
              />
              <section
                  className="grid grid-cols-1 sm:grid-cols-[auto_10px_auto] lg:grid-cols-[auto_10px_auto_10px_auto_10px_auto] content-center gap-y-5 my-7 items-center gap-2 text-center">
                <TextField
                    id="startDate"
                    label="Start Date"
                    cc-width="full"
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                />
                <span className="hidden sm:flex">-</span>
                <TextField
                    id="startTime"
                    label="Start Time"
                    cc-width="full"
                    type="time"
                    value={startTime}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setStartTime(event.target.value)
                    }}
                />
                <span className="hidden lg:flex">/</span>
                <TextField
                    id="endDate"
                    label="End Date"
                    cc-width="full"
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                />
                <span className="hidden sm:flex">-</span>
                <TextField
                    id="endTime"
                    label="End Time"
                    cc-width="full"
                    type="time"
                    value={endTime}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setEndTime(event.target.value)
                    }}
                />
              </section>
              <section>
                <Button
                    onClick={handleBlackoutClick}
                    startIcon={<AddOutlinedIcon/>}
                    cc-width="full"
                >
                  Add Blackout Period
                </Button>
              </section>
            </section>
          </div>
          <div className="flex flex-col gap-10 p-6 pb-32">
            <section>
              <h3 className="font-semibold mb-3">3. Selected Dates</h3>
              <BlackoutPeriodsList
                  blackoutPeriods={blackoutSelected}
                  onDelete={handleDeleteBlackout}
              />
            </section>
          </div>
        </div>
        <div className="fixed w-full bottom-0 z-10 border-t-2">
          <Footer
              onCancel={() => navigate('/admin/locations')}
              saveText="Save Blackout Periods"
              onSave={handleOnSaveBlackout}
          />
        </div>
      </>
  )
}

export default Blackouts
